import { InjectionToken } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { getQueryParams, updateQueryParams } from '../hls/UrlUtils';

import { lastValueFrom } from './rxjs/lastValueFrom';
import { IVbSource } from './IVbSource';
import { IVbPlaybackTokenHandlerCreate } from './IVbPlaybackTokenHandlerCreate';

export const VbPlaybackToken = new InjectionToken<IVbPlaybackTokenHandlerCreate>('VbPlaybackToken');

export class VbPlaybackTokenHandler {
	private abort$: Subject<boolean>;
	private isTokenFetching: boolean;
	private refreshIntervalRef: number;

	constructor(
		private httpClient: HttpClient,
		private playbackOption: IVbSource
	) {}

	public tokenFetchingInProgress(): boolean {
		return this.isTokenFetching;
	}

	public fetchFreshToken(): Promise<IVbSource> {
		this.isTokenFetching = true;
		return this.getPlaybackToken()
			.then(token => {
				//backend should send response as key1=value1&key2=val2
				if (token && this.playbackOption.tokenConfig) {
					this.playbackOption.src = updateQueryParams(this.playbackOption.src, getQueryParams(`?${token}`));

					if (this.refreshIntervalRef) {
						this.cancelRefreshInterval();
					}
					this.fetchTokenInInterval();
				}
				return this.playbackOption;
			})
			.finally(() => {
				this.isTokenFetching = false;
				this.abort$ = undefined;
			});
	}

	public cancelFetchToken(): void {
		if (this.isTokenFetching && this.abort$) {
			this.abort$.next(true);
		}
	}

	private sendPlaylistXhrRequest(playback: IVbSource): Promise<any> {
		return lastValueFrom(this.httpClient.get(playback.src, {
			responseType: 'text',
			withCredentials: playback.tokenConfig.withCredentials
		}));
	}

	private getPlaybackToken(): Promise<string> {
		const { tokenConfig } = this.playbackOption;

		this.abort$ = new Subject<boolean>();

		return lastValueFrom(this.httpClient.get(tokenConfig.tokenEndpoint, {
			params: {
				resourceId: tokenConfig.resourceId,
				resourceType: tokenConfig.resourceType,
				tokenName: tokenConfig.tokenName,
				path: tokenConfig.path,
				cnode: tokenConfig.cnode
			},
			responseType: 'text'
		})
			.pipe(
				takeUntil(this.abort$)
			));
	}

	private fetchTokenInInterval(): void {
		const { renewalIntervalInMins } = this.playbackOption.tokenConfig;
		//TODO: Will revisit the strategy once the happy path start working..
		this.refreshIntervalRef = window.setInterval(() => {
			this.getPlaybackToken()
				.then(response => this.sendPlaylistWithNewToken(response));
		}, renewalIntervalInMins * 60000);
	}

	private sendPlaylistWithNewToken(tokenResponse: string): void {
		this.playbackOption.src = updateQueryParams(this.playbackOption.src, getQueryParams(`?${tokenResponse}`));
		this.sendPlaylistXhrRequest(this.playbackOption);
	}

	public cancelRefreshInterval(): void {
		if (this.refreshIntervalRef) {
			window.clearInterval(this.refreshIntervalRef);
			this.refreshIntervalRef = undefined;
		}
	}
}

export function getVbPlaybackToken(
	httpClient: HttpClient
): IVbPlaybackTokenHandlerCreate {
	return (playbackOption: IVbSource) => new VbPlaybackTokenHandler(httpClient, playbackOption);
}
